/*
 * Carbisdale - CoreInput
 *
 * Keyboard- und Mouse-Handling fuer die Core Console.
 * Verarbeitet Texteingabe, Navigation, Selection und Scrolling.
 */

import {
    getWordBounds,
    isLetterOrDigit,
    codepointLengthAt,
    codepointLengthBefore
} from '../TextProcessor.js'
import {
    adjustSpansForDeleteRange, extendOrCreateSpan,
    htmlToRichText, markdownToRichText
} from '../RichTextOps.js'
import { parseMarkdownBlocks } from '../MarkdownBlockParser.js'
import { TIMING, PARAGRAPH_SPACING } from '../config.js'

// Format-Shortcut Lookup-Tabellen
const _FORMAT_SHORTCUTS = { b: 'bold', i: 'italic', u: 'underline', e: 'code' }
const _SHIFT_FORMAT_SHORTCUTS = { x: 'strikethrough' }

/**
 * CoreInput — Event-Handler der Console.
 */
export class CoreInput {
    /**
     * @param {CoreModel} model
     * @param {CoreView} view
     * @param {function} onSend - Callback wenn User Enter drueckt (text) => void
     */
    constructor(model, view, onSend) {
        this.model = model
        this.view = view
        this.onSend = onSend

        this._actionBlockedCallback = null

        // AutoCorrect-Engine (wird via setAutoCorrect() injiziert)
        this._autoCorrect = null

        // Mouse-State (Input-Drag)
        this._isDragging = false
        this._dragStartOffset = null
        this._lastClickTime = 0
        this._lastClickOffset = -1

        // Mouse-State (Output-Drag ueber Console-Entries)
        this._outputDragStart = null      // {entryIndex, offset} | null
        this._outputDragMode = false
        this._outputWordSelectMode = false
        this._outputAnchorWord = null     // {entryIndex, start, end} | null
        this._lastOutputClickTime = 0
        this._lastOutputClickKey = null   // `${entryIndex}:${offset}` fuer Klick-Folge

        // Auto-Scroll waehrend Output-Drag
        this._outputAutoScrollTimer = null
        this._outputAutoScrollDirection = 0
        this._outputAutoScrollSpeed = 0
        this._outputAutoScrollLastX = 0
        this._outputAutoScrollLastPixelX = null

        // Goal Column fuer vertikale Navigation (pixel oder char)
        this._goalColumn = null
    }

    /**
     * @returns {boolean} true, solange ein Output-Drag laeuft (zwischen
     *   MouseDown auf einem Output-Entry und MouseUp). Wird vom globalen
     *   mouseMove-Dispatch genutzt, um den Drag auch dann an die Console
     *   weiterzureichen, wenn die Maus den Core-Pane verlaesst (sonst
     *   klemmt der Auto-Scroll am oberen/unteren Pane-Rand).
     */
    isOutputDragging() {
        return this._outputDragMode === true
    }

    /**
     * Setzt den Callback fuer blockierte Aktionen (Ping-Alarm).
     * @param {function} fn
     */
    setActionBlockedCallback(fn) {
        this._actionBlockedCallback = fn
    }

    /**
     * Setzt die AutoCorrect-Engine (Dependency Injection).
     * @param {AutoCorrect} autoCorrect
     */
    setAutoCorrect(autoCorrect) {
        this._autoCorrect = autoCorrect
    }

    /** Ping-Alarm abspielen */
    _pingAlarm() {
        if (this._actionBlockedCallback) {
            this._actionBlockedCallback()
        }
    }

    // === Keyboard ===

    /**
     * Behandelt ein Keydown-Event.
     * @param {KeyboardEvent} e
     * @returns {boolean} true wenn Event behandelt wurde
     */
    handleKeydown(e) {
        const cmdKey = e.metaKey || e.ctrlKey

        // Escape mit aktiver Output-Selection: Selection clearen statt Fullscreen-Toggle
        if (e.key === 'Escape' && this.model.hasOutputSelection()) {
            this.model.clearOutputSelection()
            return true
        }

        // AutoCorrect: Pending-Replacement sichern und bei jeder Taste konsumieren
        const pendingAutoCorrect = this._autoCorrect ? this._autoCorrect.lastReplacement : null
        if (this._autoCorrect) this._autoCorrect.clearLastReplacement()

        // Lock-Zustand: Nur Navigation/Scrolling erlauben
        if (this.model.isLocked()) {
            return this._handleLockedKeydown(e)
        }

        // Undo/Redo
        if (cmdKey && !e.altKey && e.key === 'z') {
            if (e.shiftKey) {
                this.model.redo()
            } else {
                this.model.undo()
            }
            this._rebuildAndScroll()
            return true
        }

        // Enter: Absenden oder Zeilenumbruch
        if (e.key === 'Enter') {
            if (e.shiftKey) {
                // Shift+Enter: Zeilenumbruch mit Blank-Handling und Healing
                this.model.takeSnapshot()
                const text = this.model.getInputText()
                const cursor = this.model.getInputCursor()
                const trimBefore = (cursor > 0 && text[cursor - 1] === ' ') ? 1 : 0
                const trimAfter = (cursor < text.length && text[cursor] === ' ') ? 1 : 0
                if (trimBefore || trimAfter) {
                    this.model.setInputText(text.slice(0, cursor - trimBefore) + '\n' + text.slice(cursor + trimAfter))
                    this.model.setInputCursor(cursor - trimBefore + 1)
                } else {
                    this.model.insertAtCursor('\n')
                }
                this._rebuildAndScroll()
                return true
            }
            // Enter: Absenden
            const text = this.model.submitInput()
            if (text !== null && this.onSend) {
                this.onSend(text)
            }
            this._rebuildAndScroll()
            return true
        }

        // Format-Shortcuts: Cmd+B, Cmd+I, Cmd+U, Cmd+Shift+X
        if (cmdKey && !e.altKey) {
            const format = e.shiftKey
                ? _SHIFT_FORMAT_SHORTCUTS[e.key.toLowerCase()]
                : _FORMAT_SHORTCUTS[e.key.toLowerCase()]
            if (format) {
                this.model.takeSnapshot()
                this.model.applyFormat(format)
                if (this.model.isActionBlocked()) this._pingAlarm()
                this._rebuildAndScroll()
                return true
            }
        }

        // Backspace
        if (e.key === 'Backspace') {
            // AutoCorrect-Undo: Backspace direkt nach Replacement → Ersetzung rückgängig machen
            if (pendingAutoCorrect && !this.model.hasSelection()) {
                if (this.model.getInputCursor() === pendingAutoCorrect.offset) {
                    this.model.undo()
                    this._rebuildAndScroll()
                    return true
                }
            }
            if (this.model.getInputCursor() === 0 && !this.model.hasSelection()) {
                return true
            }
            this.model.takeSnapshot()
            if (this.model.hasSelection()) {
                this.model.deleteSelection()
            } else {
                const textBefore = this.model.getInputText()
                const posBefore = this.model.getInputCursor()
                const charToDelete = posBefore > 0 ? textBefore[posBefore - 1] : null
                this.model.deleteAtCursor(-1)
                // Space einfügen wenn Newline gelöscht und Wörter zusammenkleben
                if (charToDelete === '\n') {
                    this._insertSpaceIfWordsCollide()
                }
            }
            this._rebuildAndScroll()
            return true
        }

        // Delete
        if (e.key === 'Delete') {
            if (this.model.getInputCursor() >= this.model.getInputText().length &&
                !this.model.hasSelection()) {
                return true
            }
            this.model.takeSnapshot()
            if (this.model.hasSelection()) {
                this.model.deleteSelection()
            } else {
                const textBefore = this.model.getInputText()
                const posBefore = this.model.getInputCursor()
                const charToDelete = posBefore < textBefore.length ? textBefore[posBefore] : null
                this.model.deleteAtCursor(1)
                // Space einfügen wenn Newline gelöscht und Wörter zusammenkleben
                if (charToDelete === '\n') {
                    this._insertSpaceIfWordsCollide()
                }
            }
            this._rebuildAndScroll()
            return true
        }

        // Navigation
        if (this._handleNavigation(e)) return true

        // Select All
        if (cmdKey && !e.altKey && !e.shiftKey && (e.key === 'a' || e.key === 'A')) {
            this.model.setInputSelection({
                anchor: 0,
                head: this.model.getInputText().length
            })
            this.model.setInputCursor(this.model.getInputText().length)
            return true
        }

        // Zeicheneingabe (einzelnes druckbares Zeichen)
        // Alt (Option) auf dem Mac erzeugt Sonderzeichen (@, €, {, }, etc.) — nicht blockieren
        // Mac DE: Shift+Akzent-Taste liefert key='Dead' code='Equal' statt '`' — direkt umbiegen
        const charKey = (e.key === 'Dead' && e.code === 'Equal' && e.shiftKey)
            ? '`'
            : (e.key.length === 1 ? e.key : null)
        if (charKey !== null && !cmdKey) {
            this.model.takeSnapshot()
            this.model.insertAtCursor(charKey)

            // AutoCorrect: Trigger prüfen (Emoji + Format-Makros)
            if (this._autoCorrect) {
                const text = this.model.getInputText()
                const cursor = this.model.getInputCursor()
                const match = this._autoCorrect.check(text, cursor, {
                    activeFormats: this.model._activeFormats
                })
                if (match) {
                    this.model.takeSnapshot()

                    if (match.kind === 'emoji') {
                        // Emoji-Ersetzung
                        const newText = text.slice(0, match.start) + match.replacement + text.slice(match.start + match.trigger.length)
                        this.model.setInputText(newText)
                        this.model.setInputCursor(match.start + match.replacement.length)
                        this._autoCorrect.lastReplacement = {
                            offset: match.start + match.replacement.length,
                            original: match.trigger,
                            replacement: match.replacement
                        }
                    } else if (match.kind === 'formatOn') {
                        // Marker entfernen, Format aktivieren (wie Editor)
                        const markerLen = match.marker.length
                        const newText = text.slice(0, match.start) + text.slice(match.start + markerLen)
                        this.model.inputFormats = adjustSpansForDeleteRange(
                            this.model.inputFormats, match.start, match.start + markerLen)
                        // Getipptes Zeichen retroaktiv formatieren (wurde VOR dem Check ohne Format eingefügt)
                        this.model.inputFormats = extendOrCreateSpan(
                            this.model.inputFormats, match.start, match.start + 1, match.formatType)
                        this.model.setInputText(newText)
                        this.model.setInputCursor(cursor - markerLen)
                        this.model._activeFormats.add(match.formatType)
                    } else if (match.kind === 'formatOff') {
                        // Marker entfernen, Format deaktivieren (wie Editor)
                        const markerLen = match.marker.length
                        const newText = text.slice(0, match.start) + text.slice(match.start + markerLen)
                        this.model.inputFormats = adjustSpansForDeleteRange(
                            this.model.inputFormats, match.start, match.start + markerLen)
                        this.model.setInputText(newText)
                        this.model.setInputCursor(cursor - markerLen)
                        this.model._activeFormats.delete(match.formatType)
                    }
                }
            }

            this._goalColumn = null
            this._rebuildAndScroll()
            return true
        }

        return false
    }

    /**
     * Behandelt Tastendruecke im Lock-Zustand.
     * @param {KeyboardEvent} e
     * @returns {boolean}
     */
    _handleLockedKeydown(e) {
        // Escape: nicht behandeln — Fullscreen-Toggle im globalen Handler
        // (Agent-Abbruch läuft jetzt ausschliesslich ueber das (!) Control)
        if (e.key === 'Escape') {
            return false
        }

        // Navigation (Scrolling) ist erlaubt
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
            e.key === 'PageUp' || e.key === 'PageDown') {
            return this._handleNavigation(e)
        }

        // Modifier-only Keys ignorieren
        if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Tab'].includes(e.key)) {
            return false
        }

        // Alle anderen Tasten → Ping
        this._pingAlarm()
        return true
    }

    /**
     * Behandelt Navigation (Pfeiltasten, Home/End, etc.).
     * @param {KeyboardEvent} e
     * @returns {boolean}
     */
    _handleNavigation(e) {
        const cmdKey = e.metaKey || e.ctrlKey
        const shift = e.shiftKey

        switch (e.key) {
            case 'ArrowLeft':
                this._moveCursor(-1, shift, cmdKey)
                this._goalColumn = null
                return true
            case 'ArrowRight':
                this._moveCursor(1, shift, cmdKey)
                this._goalColumn = null
                return true
            case 'ArrowUp':
                this._moveCursorVertical(-1, shift, cmdKey)
                return true
            case 'ArrowDown':
                this._moveCursorVertical(1, shift, cmdKey)
                return true
            case 'Home':
                this._moveToLineEdge(-1, shift, cmdKey)
                this._goalColumn = null
                return true
            case 'End':
                this._moveToLineEdge(1, shift, cmdKey)
                this._goalColumn = null
                return true
            default:
                return false
        }
    }

    /**
     * Bewegt den Cursor horizontal.
     * @param {number} direction - -1 oder +1
     * @param {boolean} shift - Selection erweitern
     * @param {boolean} cmd - Zum Zeilenanfang/-ende
     */
    _moveCursor(direction, shift, cmd) {
        const text = this.model.getInputText()
        let newPos = this.model.getInputCursor()

        if (cmd) {
            // Cmd+Links/Rechts: Zum Anfang/Ende der aktuellen Wrap-Zeile
            const screenPos = this.view.inputToScreen(newPos)
            if (screenPos) {
                const lineIndex = screenPos.lineIndex
                if (lineIndex >= 0 && lineIndex < this.view.lines.length) {
                    const line = this.view.lines[lineIndex]
                    newPos = (direction === -1) ? line.startOffset : line.endOffset
                }
            }
        } else {
            // Einfache Bewegung (Surrogate Pair: ganzen Codepoint überspringen)
            if (!shift && this.model.hasSelection()) {
                // Selection collapse
                const sel = this.model.getInputSelection()
                newPos = (direction === -1)
                    ? Math.min(sel.anchor, sel.head)
                    : Math.max(sel.anchor, sel.head)
            } else {
                const step = (direction === -1)
                    ? codepointLengthBefore(text, newPos)
                    : codepointLengthAt(text, newPos)
                newPos = Math.max(0, Math.min(text.length, newPos + direction * step))
            }
        }

        this._updateCursorWithSelection(newPos, shift)
    }

    /**
     * Bewegt den Cursor vertikal (innerhalb umbrochener Eingabe).
     * @param {number} direction - -1 (hoch) oder +1 (runter)
     * @param {boolean} shift - Selection erweitern
     * @param {boolean} cmd - Zum Anfang/Ende des gesamten Inputs
     */
    _moveCursorVertical(direction, shift, cmd) {
        if (cmd) {
            // Cmd+Hoch/Runter: Zum Anfang/Ende des gesamten Inputs
            const newPos = (direction === -1) ? 0 : this.model.getInputText().length
            this._updateCursorWithSelection(newPos, shift)
            this._goalColumn = null
            this._rebuildAndScroll()
            return
        }

        const cursorPos = this.model.getInputCursor()
        const screenPos = this.view.inputToScreen(cursorPos)
        if (!screenPos) return

        const lineIndex = screenPos.lineIndex
        const targetLineIndex = lineIndex + direction

        // Suche naechste Input-Zeile in Richtung
        let targetLine = null
        let targetIdx = targetLineIndex
        while (targetIdx >= 0 && targetIdx < this.view.lines.length) {
            if (this.view.lines[targetIdx].lineType === 'INPUT') {
                targetLine = this.view.lines[targetIdx]
                break
            }
            targetIdx += direction
        }

        if (!targetLine) {
            // Keine weitere Input-Zeile → Anfang oder Ende
            const newPos = (direction === -1) ? 0 : this.model.getInputText().length
            this._updateCursorWithSelection(newPos, shift)
            this._goalColumn = null
            return
        }

        // Goal Column: Proportional = pixelX, Mono = charX
        const isProportional = this.view.renderer && this.view.renderer.isProportional()
        if (this._goalColumn === null) {
            if (isProportional && screenPos.pixelX !== undefined) {
                this._goalColumn = { type: 'pixel', value: screenPos.pixelX }
            } else {
                this._goalColumn = { type: 'char', value: screenPos.x - this.view.lines[lineIndex].prefix.length }
            }
        }

        // Ziel-Position per Goal Column bestimmen
        let clampedOffset
        if (isProportional && this._goalColumn.type === 'pixel') {
            // PositionMap-basierte Aufloesung
            const targetPosMap = this.view._getPositionMap(targetIdx)
            if (targetPosMap) {
                const goalPixelX = this._goalColumn.value - targetLine.prefix.length * this.view.renderer.charWidth
                const displayIndex = targetPosMap.getCharIndexAtX(Math.max(0, goalPixelX))
                clampedOffset = this.view._displayIndexToDataOffset(targetLine, displayIndex)
            } else {
                const textX = Math.round(this._goalColumn.value / this.view.renderer.charWidth) - targetLine.prefix.length
                clampedOffset = targetLine.startOffset + Math.min(Math.max(0, textX), targetLine.text.length)
            }
        } else {
            const textX = this._goalColumn.value
            clampedOffset = targetLine.startOffset + Math.min(textX, targetLine.text.length)
        }
        clampedOffset = Math.min(clampedOffset, this.model.getInputText().length)

        this._updateCursorWithSelection(clampedOffset, shift)
        this._rebuildAndScroll()
    }

    /**
     * Bewegt den Cursor zum Zeilenanfang/-ende.
     * @param {number} direction - -1 (Home) oder +1 (End)
     * @param {boolean} shift - Selection erweitern
     * @param {boolean} cmd - Cmd+Home/End = Inputanfang/-ende
     */
    _moveToLineEdge(direction, shift, cmd) {
        if (cmd) {
            const newPos = (direction === -1) ? 0 : this.model.getInputText().length
            this._updateCursorWithSelection(newPos, shift)
            this._rebuildAndScroll()
            return
        }

        // Zum Anfang/Ende der aktuellen Wrap-Zeile
        const cursorPos = this.model.getInputCursor()
        const screenPos = this.view.inputToScreen(cursorPos)
        if (!screenPos) return

        const lineIndex = screenPos.lineIndex
        if (lineIndex >= 0 && lineIndex < this.view.lines.length) {
            const line = this.view.lines[lineIndex]
            const newPos = (direction === -1) ? line.startOffset : line.endOffset
            this._updateCursorWithSelection(newPos, shift)
        }
    }

    /**
     * Aktualisiert Cursor-Position mit optionaler Selection-Erweiterung.
     * @param {number} newPos
     * @param {boolean} extendSelection
     */
    _updateCursorWithSelection(newPos, extendSelection) {
        const oldPos = this.model.getInputCursor()
        if (extendSelection) {
            const sel = this.model.getInputSelection()
            const anchor = sel ? sel.anchor : oldPos
            this.model.setInputSelection({ anchor, head: newPos })
        } else {
            this.model.clearInputSelection()
        }
        this.model.setInputCursor(newPos)
    }

    /**
     * Fügt ein Space ein wenn nach einer Newline-Löschung Wörter zusammenkleben.
     */
    _insertSpaceIfWordsCollide() {
        const text = this.model.getInputText()
        const pos = this.model.getInputCursor()
        if (pos > 0 && pos < text.length) {
            if (isLetterOrDigit(text[pos - 1]) && isLetterOrDigit(text[pos])) {
                this.model.setInputText(text.slice(0, pos) + ' ' + text.slice(pos))
                // Cursor bleibt links vom Space (am Verbindungspunkt)
            }
        }
    }

    /** Rebuild lines und scroll zum Cursor */
    _rebuildAndScroll() {
        this.view.rebuildLines()
        this.view.ensureCursorVisible()
    }

    // === Clipboard ===

    /**
     * Behandelt Copy. Output-Selection hat Vorrang vor Input-Selection
     * (Mutex auf Modell-Ebene — beide zusammen kommen nicht vor, aber
     * defensiv pruefen wir Output zuerst).
     * @returns {string} Der kopierte Text
     */
    handleCopy() {
        if (this.model.hasOutputSelection()) return this.model.getSelectedOutputText()
        if (this.model.hasSelection()) return this.model.getSelectedText()
        return ''
    }

    /**
     * Behandelt Cut. Output-Entries sind immutable — Cut auf Output-Selection
     * ist no-op (kein Clipboard-Schreiben). Nur Input-Selection wird geschnitten.
     * @returns {string} Der ausgeschnittene Text
     */
    handleCut() {
        if (!this.model.hasSelection()) return ''
        this.model.takeSnapshot()
        const text = this.model.getSelectedText()
        this.model.deleteSelection()
        this._rebuildAndScroll()
        return text
    }

    /**
     * Behandelt Paste.
     *
     * Format-Quellen-Reihenfolge (analog Editor-Paste):
     *  1. HTML aus Clipboard → `htmlToRichText` (Word/Notes/Editor-RT-Copy)
     *  2. Plain-Text mit Markdown-Markern → `markdownToRichText` (z.B. AI-
     *     Output, der ja Markdown ist — sonst landen `**bold**` oder
     *     `` `code` `` als Plaintext-Marker im Input)
     *  3. Sonst: Plain-Text-Insert ohne Spans
     *
     * Mehrzeilige Pastes werden zeilenweise konvertiert; die Block-Format-
     * Felder (heading/list/blockquote) sind in der Console-Eingabe nicht
     * unterstuetzt — nur Inline-Formate landen auf den input-Spans.
     *
     * @param {string} text - Plain-Text aus dem Clipboard
     * @param {string|null} [html] - Optional HTML aus dem Clipboard
     */
    handlePaste(text, html = null) {
        if (!text && !html) return
        this.model.takeSnapshot()

        const insertOffset = this.model.inputCursor
        let pasteText = text || ''
        let formats = []

        if (html) {
            const parsed = htmlToRichText(html)
            // Mehrere Bloecke per "\n" verbinden, Spans pro Block in den
            // gesamten pasteText-Raum verschieben.
            const lines = []
            for (const p of parsed) {
                const offsetInPaste = lines.join('\n').length + (lines.length > 0 ? 1 : 0)
                for (const f of (p.formats || [])) {
                    formats.push({
                        start: offsetInPaste + f.start,
                        end: offsetInPaste + f.end,
                        type: f.type
                    })
                }
                lines.push(p.text)
            }
            pasteText = lines.join('\n')
        } else if (text) {
            // Markdown-Marker im Plaintext erkennen (z.B. AI-Output).
            const lines = text.split('\n')
            const cleanLines = []
            for (const line of lines) {
                const offsetInPaste = cleanLines.join('\n').length + (cleanLines.length > 0 ? 1 : 0)
                const rt = markdownToRichText(line)
                for (const f of (rt.formats || [])) {
                    formats.push({
                        start: offsetInPaste + f.start,
                        end: offsetInPaste + f.end,
                        type: f.type
                    })
                }
                cleanLines.push(rt.text)
            }
            pasteText = cleanLines.join('\n')
        }

        this.model.insertAtCursor(pasteText)

        // Spans an den Insert-Bereich verschieben und an inputFormats anhaengen
        if (formats.length > 0) {
            for (const f of formats) {
                this.model.inputFormats = extendOrCreateSpan(
                    this.model.inputFormats,
                    insertOffset + f.start,
                    insertOffset + f.end,
                    f.type
                )
            }
        }

        this._rebuildAndScroll()
    }

    // === Mouse ===

    /**
     * Behandelt Mouse-Down im Console-Pane.
     * @param {number} x - X relativ zum innerBounds
     * @param {number} y - Y relativ zum innerBounds
     * @param {boolean} shiftKey
     * @param {number|null} pixelX - Pixel-Position (proportional)
     */
    handleMouseDown(x, y, shiftKey, pixelX = null, pixelY = null) {
        const inputOffset = this.view.screenToInput(x, y, pixelX, pixelY)
        if (inputOffset === null) {
            // Klick ausserhalb Input → Output-Entry oder leerer Bereich
            const entryInfo = this.view.screenToEntry(x, y, pixelX, pixelY)
            if (entryInfo) {
                const entries = this.model.getEntries()
                const entry = entries[entryInfo.entryIndex]
                // THINKING-Toggle:
                //  - collapsed: jeder Klick togglet (Marker '[...]' aufklappen)
                //  - expanded:  nur Klick auf Prefix '[...] ' togglet — der Text
                //               selbst ist selektierbar
                if (entry && entry.type === 'THINKING' &&
                    (entry.collapsed || entryInfo.inPrefix)) {
                    // Toggle setzt preserveScroll-Flag; der Render-Cycle macht
                    // rebuildLines + scrollBy(0) (Viewport-Normalisierung ohne
                    // scrollToBottom).
                    this.model.toggleEntryCollapsed(entry.id)
                    return
                }
                // Sonst: Output-Selection-Pfad (Doppel-/Dreifachklick + Drag-Start)
                if (entry) this._beginOutputClick(entryInfo, shiftKey)
            } else {
                // Klick in komplett leeren Bereich → Output-Selection clearen
                this.model.clearOutputSelection()
            }
            return
        }
        if (inputOffset !== null) {
            const now = Date.now()

            // Doppelklick-Erkennung (Wort selektieren)
            if (now - this._lastClickTime < TIMING.doubleClickThreshold && inputOffset === this._lastClickOffset) {
                this._selectWordAtOffset(inputOffset)
                this._lastClickTime = 0  // Reset: kein Dreifachklick
                return
            }
            this._lastClickTime = now
            this._lastClickOffset = inputOffset

            // Klick im Input-Bereich
            if (shiftKey && this.model.getInputSelection()) {
                const sel = this.model.getInputSelection()
                this.model.setInputSelection({ anchor: sel.anchor, head: inputOffset })
            } else if (shiftKey) {
                this.model.setInputSelection({
                    anchor: this.model.getInputCursor(),
                    head: inputOffset
                })
            } else {
                this.model.clearInputSelection()
            }
            this.model.setInputCursor(inputOffset)
            this._isDragging = true
            this._dragStartOffset = inputOffset
        }
    }

    /**
     * Selektiert das Wort an der gegebenen Position.
     * Nutzt Unicode-Wort-Erkennung und expandiert ueber Format-Marker.
     * @param {number} offset
     */
    _selectWordAtOffset(offset) {
        const text = this.model.getInputText()
        if (text.length === 0) return

        const bounds = getWordBounds(text, offset)
        if (!bounds) return

        if (bounds.start < bounds.end) {
            this.model.setInputSelection({ anchor: bounds.start, head: bounds.end })
            this.model.setInputCursor(bounds.end)
        }
    }

    // === Output-Selection Helpers ===

    /**
     * Liefert den Text, auf dem `offset` operiert. Bei AI-Entries mit
     * gueltigem `blockId` ist das der Block-Text aus `parseMarkdownBlocks`,
     * sonst der ganze entry.text (non-AI oder blockId === -1).
     * @param {number} entryIndex
     * @param {number} blockId
     * @returns {string}
     */
    _getOutputBlockText(entryIndex, blockId) {
        const entry = this.model.getEntries()[entryIndex]
        if (!entry) return ''
        if (entry.type === 'AI' && blockId >= 0) {
            const blocks = parseMarkdownBlocks(entry.text)
            return (blocks[blockId] && blocks[blockId].text) || ''
        }
        return entry.text || ''
    }

    /**
     * Selektiert das Wort an einem Punkt innerhalb eines Output-Entry-Blocks.
     * @param {{entryIndex, blockId, offset}} entryInfo
     */
    _selectWordInEntry(entryInfo) {
        const text = this._getOutputBlockText(entryInfo.entryIndex, entryInfo.blockId)
        if (!text) return
        const bounds = getWordBounds(text, entryInfo.offset)
        if (!bounds || bounds.start >= bounds.end) return
        this.model.setOutputSelection({
            anchor: { entryIndex: entryInfo.entryIndex, blockId: entryInfo.blockId, offset: bounds.start },
            head:   { entryIndex: entryInfo.entryIndex, blockId: entryInfo.blockId, offset: bounds.end }
        })
    }

    /**
     * Selektiert den gesamten Output-Entry. Bei AI-Multi-Block-Entries
     * laeuft die Selection vom ersten Block (Offset 0) bis zum letzten
     * Block (Block-Text-Ende).
     * @param {number} entryIndex
     */
    _selectEntireEntry(entryIndex) {
        const entry = this.model.getEntries()[entryIndex]
        if (!entry) return
        if (entry.type === 'AI') {
            const blocks = parseMarkdownBlocks(entry.text)
            if (blocks.length === 0) return
            const lastIdx = blocks.length - 1
            const lastText = (blocks[lastIdx] && blocks[lastIdx].text) || ''
            this.model.setOutputSelection({
                anchor: { entryIndex, blockId: 0, offset: 0 },
                head:   { entryIndex, blockId: lastIdx, offset: lastText.length }
            })
            return
        }
        this.model.setOutputSelection({
            anchor: { entryIndex, blockId: -1, offset: 0 },
            head:   { entryIndex, blockId: -1, offset: entry.text.length }
        })
    }

    /**
     * Zaehlt grob die "Wortuebergaenge" zwischen zwei Output-Punkten.
     * Wird fuer den Word-Snap-Mode genutzt (Schwelle: 3). Ueber Block-
     * oder Entry-Grenzen hinweg zaehlt jeder Wechsel als zusaetzliches Wort.
     * @param {{entryIndex, blockId, offset}} a
     * @param {{entryIndex, blockId, offset}} b
     * @returns {number}
     */
    _countTouchedOutputWords(a, b) {
        const cmp = a.entryIndex !== b.entryIndex
            ? (a.entryIndex - b.entryIndex)
            : a.blockId !== b.blockId
                ? (a.blockId - b.blockId)
                : (a.offset - b.offset)
        const fwd = cmp <= 0
        const start = fwd ? a : b
        const end = fwd ? b : a
        const entries = this.model.getEntries()
        let count = 0
        for (let i = start.entryIndex; i <= end.entryIndex && i < entries.length; i++) {
            const e = entries[i]
            if (!e) continue
            // Block-Liste: AI mit blockIds, sonst Single-Block-Pseudoliste mit blockId=-1
            const blocks = (e.type === 'AI')
                ? parseMarkdownBlocks(e.text).map((bl, bi) => ({ id: bi, text: bl.text || '' }))
                : [{ id: -1, text: e.text || '' }]
            const firstBlock = (i === start.entryIndex) ? start.blockId : (blocks[0] ? blocks[0].id : -1)
            const lastBlock = (i === end.entryIndex) ? end.blockId : (blocks[blocks.length - 1] ? blocks[blocks.length - 1].id : -1)
            for (const bl of blocks) {
                if (bl.id < firstBlock || bl.id > lastBlock) continue
                const sliceStart = (i === start.entryIndex && bl.id === start.blockId) ? start.offset : 0
                const sliceEnd   = (i === end.entryIndex   && bl.id === end.blockId)   ? end.offset   : bl.text.length
                if (sliceEnd <= sliceStart) continue
                const slice = bl.text.substring(sliceStart, sliceEnd)
                const matches = slice.match(/\s+/g)
                count += 1 + (matches ? matches.length : 0)
                if (bl.id < lastBlock || i < end.entryIndex) count += 1  // Block-/Entry-Grenze
            }
        }
        return count
    }

    /**
     * Liefert das Wort-Bounds-Objekt fuer einen Output-Punkt.
     * @param {{entryIndex, blockId, offset}} entryInfo
     * @returns {{entryIndex, blockId, start, end}|null}
     */
    _getOutputWordBounds(entryInfo) {
        const text = this._getOutputBlockText(entryInfo.entryIndex, entryInfo.blockId)
        if (!text) return null
        const b = getWordBounds(text, entryInfo.offset)
        if (!b || b.start >= b.end) return null
        return { entryIndex: entryInfo.entryIndex, blockId: entryInfo.blockId, start: b.start, end: b.end }
    }

    /**
     * Behandelt einen Klick auf einen Output-Entry (Single/Doppel/Dreifach
     * + Drag-Start). Collapsed THINKING wird vom Aufrufer abgefangen.
     * Triple-Click: ganzes Entry. Double-Click: Wort. Single-Click: Drag.
     * @param {{entryIndex: number, blockId: number, offset: number}} entryInfo
     * @param {boolean} shiftKey
     */
    _beginOutputClick(entryInfo, shiftKey) {
        const now = Date.now()
        const blockId = entryInfo.blockId
        const key = `${entryInfo.entryIndex}:${blockId}:${entryInfo.offset}`
        const isFastFollowUp = (now - this._lastOutputClickTime) < TIMING.doubleClickThreshold
            && this._lastOutputClickKey === key

        // Shift+Klick: bestehende Output-Selection auf Klick-Punkt erweitern
        if (shiftKey && this.model.hasOutputSelection()) {
            const sel = this.model.getOutputSelection()
            this.model.setOutputSelection({
                anchor: sel.anchor,
                head: { entryIndex: entryInfo.entryIndex, blockId, offset: entryInfo.offset }
            })
            this._lastOutputClickTime = now
            this._lastOutputClickKey = key
            return
        }

        if (isFastFollowUp) {
            // Bei jeder schnellen Folge zaehlt der Streak hoch (max 3, dann Reset)
            this._outputClickStreak = (this._outputClickStreak || 1) + 1
            if (this._outputClickStreak >= 3) {
                this._selectEntireEntry(entryInfo.entryIndex)
                this._outputClickStreak = 0
                this._lastOutputClickTime = 0
                this._lastOutputClickKey = null
                return
            }
            // Streak == 2 → Wort selektieren
            this._selectWordInEntry(entryInfo)
            this._lastOutputClickTime = now
            this._lastOutputClickKey = key
            return
        }

        // Normaler erster Klick: Drag-Start, collapsed Output-Selection setzen
        this._outputClickStreak = 1
        this._lastOutputClickTime = now
        this._lastOutputClickKey = key
        this._outputDragStart = { entryIndex: entryInfo.entryIndex, blockId, offset: entryInfo.offset }
        this._outputDragMode = true
        this._outputWordSelectMode = false
        this._outputAnchorWord = null
        this.model.setOutputSelection({
            anchor: { ...this._outputDragStart },
            head: { ...this._outputDragStart }
        })
    }

    /**
     * Aktualisiert die Output-Selection waehrend eines Drags auf den
     * gegebenen Output-Punkt. Aktiviert ggf. Word-Snap-Mode (≥3 Worte).
     * @param {{entryIndex: number, blockId: number, offset: number}} headInfo
     */
    _updateOutputDrag(headInfo) {
        if (!this._outputDragStart) return

        const touched = this._countTouchedOutputWords(this._outputDragStart, headInfo)

        if (touched >= 3 && !this._outputWordSelectMode) {
            this._outputWordSelectMode = true
            this._outputAnchorWord = this._getOutputWordBounds(this._outputDragStart)
        } else if (touched < 3 && this._outputWordSelectMode) {
            this._outputWordSelectMode = false
            this._outputAnchorWord = null
        }

        if (this._outputWordSelectMode && this._outputAnchorWord) {
            // Word-Snap: Anker und Cursor jeweils auf Wortgrenzen clampen
            const anchorWord = this._outputAnchorWord
            const headWord = this._getOutputWordBounds(headInfo)
            // Lex-Vergleich (entryIndex, blockId, offset) zwischen head und anchorWord-Start
            const cmp = headInfo.entryIndex !== anchorWord.entryIndex
                ? (headInfo.entryIndex - anchorWord.entryIndex)
                : headInfo.blockId !== anchorWord.blockId
                    ? (headInfo.blockId - anchorWord.blockId)
                    : (headInfo.offset - anchorWord.start)
            const fwd = cmp >= 0
            let anchor, head
            if (fwd) {
                anchor = { entryIndex: anchorWord.entryIndex, blockId: anchorWord.blockId, offset: anchorWord.start }
                head = headWord
                    ? { entryIndex: headWord.entryIndex, blockId: headWord.blockId, offset: headWord.end }
                    : { entryIndex: headInfo.entryIndex, blockId: headInfo.blockId, offset: headInfo.offset }
            } else {
                anchor = { entryIndex: anchorWord.entryIndex, blockId: anchorWord.blockId, offset: anchorWord.end }
                head = headWord
                    ? { entryIndex: headWord.entryIndex, blockId: headWord.blockId, offset: headWord.start }
                    : { entryIndex: headInfo.entryIndex, blockId: headInfo.blockId, offset: headInfo.offset }
            }
            this.model.setOutputSelection({ anchor, head })
        } else {
            this.model.setOutputSelection({
                anchor: { ...this._outputDragStart },
                head: { ...headInfo }
            })
        }
    }

    /**
     * Behandelt Mouse-Move (Drag) — Input ODER Output.
     * @param {number} x
     * @param {number} y
     * @param {number|null} pixelX - Pixel-Position (proportional)
     */
    handleMouseMove(x, y, pixelX = null, pixelY = null) {
        // Output-Drag hat Vorrang, falls aktiv
        if (this._outputDragMode) {
            // Auto-Scroll bei Verlassen des Viewports nach oben/unten
            if (y < 0) {
                this._startOutputAutoScroll(-1, Math.abs(y), x, pixelX)
                return
            } else if (y >= this.view.height) {
                this._startOutputAutoScroll(1, y - this.view.height + 1, x, pixelX)
                return
            } else {
                this._stopOutputAutoScroll()
            }
            const head = this._resolveOutputHead(x, y, pixelX, pixelY)
            if (head) this._updateOutputDrag(head)
            return
        }

        if (!this._isDragging) return

        const inputOffset = this.view.screenToInput(x, y, pixelX, pixelY)
        if (inputOffset !== null) {
            this.model.setInputSelection({
                anchor: this._dragStartOffset,
                head: inputOffset
            })
            this.model.setInputCursor(inputOffset)
        }
    }

    /**
     * Berechnet den Output-Head-Punkt fuer eine Mausposition. Wenn die
     * Position keinen Output-Entry trifft (z.B. INPUT-Zeile, BLANK), wird
     * an den letzten gueltigen Punkt geclampt: bei "vor dem ersten Entry"
     * an dessen Anfang, bei "nach dem letzten Entry / in INPUT" an das
     * Ende des letzten Entries.
     * @param {number} x
     * @param {number} y
     * @param {number|null} pixelX
     * @returns {{entryIndex: number, blockId: number, offset: number}|null}
     */
    _resolveOutputHead(x, y, pixelX, pixelY = null) {
        const entryInfo = this.view.screenToEntry(x, y, pixelX, pixelY)
        if (entryInfo) return entryInfo
        // Kein Entry getroffen → an Drag-Start oder letzten Entry clampen
        const entries = this.model.getEntries()
        if (entries.length === 0) return null
        const inputOffset = this.view.screenToInput(x, y, pixelX, pixelY)
        if (inputOffset !== null) {
            // Maus unterhalb der Entries / ueber dem Input → ans Ende clampen
            const lastIdx = entries.length - 1
            const last = entries[lastIdx]
            if (last.type === 'AI') {
                const blocks = parseMarkdownBlocks(last.text)
                const bIdx = Math.max(0, blocks.length - 1)
                const bText = (blocks[bIdx] && blocks[bIdx].text) || ''
                return { entryIndex: lastIdx, blockId: bIdx, offset: bText.length }
            }
            return { entryIndex: lastIdx, blockId: -1, offset: last.text.length }
        }
        // Maus oberhalb des ersten Entries → an dessen Anfang
        const first = entries[0]
        const firstBlockId = (first.type === 'AI') ? 0 : -1
        return { entryIndex: 0, blockId: firstBlockId, offset: 0 }
    }

    /**
     * Behandelt Mouse-Up — Input ODER Output.
     */
    handleMouseUp() {
        this._isDragging = false
        this._dragStartOffset = null
        if (this._outputDragMode) {
            this._outputDragMode = false
            this._outputDragStart = null
            this._outputWordSelectMode = false
            this._outputAnchorWord = null
            this._stopOutputAutoScroll()
        }
    }

    /**
     * Behandelt Mouse-Wheel/Trackpad/Touch (Pixel-genau).
     * Gleiche Mechanik wie EditorInput.handleMouseWheel — der Pixel-Pfad in
     * CoreView.scrollByPixels entscheidet intern: Proportional sub-pixel,
     * Monospace grid-based (via Akkumulator).
     * @param {number} deltaY
     * @param {number} deltaMode - 0=Pixel (häufig), 1=Lines (Firefox), 2=Pages
     */
    handleWheel(deltaY, deltaMode) {
        const charHeight = this.view.renderer?.charHeight || 24
        let pixels
        if (deltaMode === 1) {
            // LINE-Mode: eine "Zeile" ≈ scrollPixelsPerLine Pixel (Firefox-Konvention)
            pixels = deltaY * TIMING.scrollPixelsPerLine
        } else if (deltaMode === 2) {
            // PAGE-Mode: eine "Seite" = Viewport-Höhe
            pixels = deltaY * (this.view.height || 20) * charHeight
        } else {
            // PIXEL-Mode: 1:1 durchreichen
            pixels = deltaY
        }

        // Sicherung gegen riesige Bursts
        const maxPx = TIMING.scrollMaxLinesPerEvent * charHeight
        pixels = Math.sign(pixels) * Math.min(Math.abs(pixels), maxPx)

        if (pixels !== 0) this.view.scrollByPixels(pixels)
    }

    // === Auto-Scroll waehrend Output-Drag ===

    /**
     * Startet Auto-Scroll waehrend Output-Drag (Maus ueber Viewport-Rand).
     * @param {number} direction - -1 (hoch) oder +1 (runter)
     * @param {number} distance - Abstand vom Viewport-Rand in Zeilen
     * @param {number} screenX
     * @param {number|null} pixelX
     */
    _startOutputAutoScroll(direction, distance, screenX, pixelX) {
        this._outputAutoScrollDirection = direction
        this._outputAutoScrollSpeed = distance
        this._outputAutoScrollLastX = screenX
        this._outputAutoScrollLastPixelX = pixelX
        if (this._outputAutoScrollTimer !== null) return  // Timer laeuft schon
        this._outputAutoScrollTimer = setInterval(() => this._outputAutoScrollTick(), 80)
    }

    /** Stoppt den Output-Auto-Scroll-Timer. */
    _stopOutputAutoScroll() {
        if (this._outputAutoScrollTimer !== null) {
            clearInterval(this._outputAutoScrollTimer)
            this._outputAutoScrollTimer = null
        }
        this._outputAutoScrollDirection = 0
        this._outputAutoScrollSpeed = 0
    }

    /**
     * Auto-Scroll-Schritt: Viewport scrollen + Output-Selection-Head erweitern.
     * Geschwindigkeit waechst nichtlinear mit dem Abstand vom Rand.
     */
    _outputAutoScrollTick() {
        if (!this._outputDragMode) {
            this._stopOutputAutoScroll()
            return
        }
        // Raketen-Selektion: exponentiell schneller je weiter vom Rand
        const d = this._outputAutoScrollSpeed
        const speed = d >= 4 ? 40 : d >= 3 ? 10 : d >= 2 ? 3 : 1
        this.view.scrollBy(this._outputAutoScrollDirection * speed)

        // Position am Viewport-Rand bestimmen, neuen Head ableiten
        const edgeY = this._outputAutoScrollDirection < 0 ? 0 : this.view.height - 1
        const head = this._resolveOutputHead(this._outputAutoScrollLastX, edgeY, this._outputAutoScrollLastPixelX)
        if (head) this._updateOutputDrag(head)
    }
}
